import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  soalInterpretasiBentuk, getSoalInterpretasiBentuk, hitungSkorInterpretasiBentuk,
  URUTAN_DIMENSI_BENTUK, NAMA_DIMENSI_BENTUK, DISCLAIMER_INTERPRETASI_BENTUK,
} from './bankSoalInterpretasiBentuk';
import { catatHasilTes } from './progressTracker';
import { useCegahKeluarTes } from './cegahKeluarTes';
import { bacaProgresTes, hapusProgresTes, useAutosaveTes } from './useResumeTes';

const TOTAL_SOAL  = 10;
const WAKTU_TOTAL = 12 * 60; // 12 menit — cepat, cukup lihat & pilih

function formatWaktu(detik) {
  const m = Math.floor(detik / 60).toString().padStart(2, '0');
  const s = (detik % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

// Progres tersimpan menyimpan urutan opsi APA ADANYA (bukan lookup ulang
// dari bank) supaya posisi opsi tidak berubah lagi saat resume — bank
// aslinya cuma dipakai untuk cocokkan id soal.
function soalDariMetaIB(meta) {
  const byId = {};
  soalInterpretasiBentuk.forEach(s => { byId[s.id] = s; });
  return (meta || []).map(m => (byId[m.id] ? { id: m.id, gambar: byId[m.id].gambar, opsi: m.opsi } : null)).filter(Boolean);
}

function BannerLanjutkanIB({ progresTersimpan, onLanjutkan, onMulaiBaru }) {
  if (!progresTersimpan) return null;
  const totalSoal = progresTersimpan.soalMeta?.length || 0;
  return (
    <div className="rounded-xl p-4 mb-5 border-2 border-purple-400 bg-purple-50">
      <p className="text-xs font-bold uppercase tracking-wide text-purple-700 mb-1">⏸️ Ada Pengerjaan Sebelumnya</p>
      <p className="text-sm text-purple-800 mb-3">
        Baru sampai gambar <b>{(progresTersimpan.indeks || 0) + 1}/{totalSoal}</b>, sisa waktu <b>{formatWaktu(progresTersimpan.waktuSisa || 0)}</b>.
      </p>
      <div className="flex gap-2">
        <button onClick={onLanjutkan} className="flex-1 py-2 rounded-lg bg-purple-600 text-white text-sm font-bold hover:bg-purple-700 transition">
          Lanjutkan
        </button>
        <button onClick={onMulaiBaru} className="flex-1 py-2 rounded-lg border border-purple-300 text-purple-600 text-sm font-semibold hover:bg-purple-100 transition">
          Mulai Baru
        </button>
      </div>
    </div>
  );
}

function LayarIntro({ onMulai, onBack, tryoutMode, progresTersimpan, onLanjutkan, onMulaiBaru }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-lg w-full p-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-3xl">🔮</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Interpretasi Bentuk</h1>
          <p className="text-gray-400 text-sm mt-1">
            {tryoutMode ? 'Bagian dari Tryout Resmi' : 'Latihan menebak makna bentuk abstrak'}
          </p>
        </div>

        <BannerLanjutkanIB progresTersimpan={progresTersimpan} onLanjutkan={onLanjutkan} onMulaiBaru={onMulaiBaru} />

        <div className="rounded-xl p-4 mb-5 border-2 border-purple-200 bg-purple-50">
          <p className="text-xs font-bold uppercase tracking-wide text-purple-700 mb-1">📌 {tryoutMode ? 'Bagian Tryout' : 'Info Modul'}</p>
          <p className="text-sm text-purple-800 leading-relaxed">
            Akan ditampilkan {TOTAL_SOAL} gambar abstrak simetris. Tidak ada jawaban benar/salah — pilih saja opsi yang paling mendekati apa yang kamu lihat pertama kali.
          </p>
        </div>

        <div className="rounded-xl p-4 mb-5 border border-amber-200 bg-amber-50">
          <p className="text-[11px] text-amber-700 leading-relaxed">⚠️ {DISCLAIMER_INTERPRETASI_BENTUK}</p>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-5 grid grid-cols-3 gap-3 text-center">
          <div><p className="text-lg font-bold text-gray-800">{TOTAL_SOAL}</p><p className="text-xs text-gray-500">Gambar</p></div>
          <div><p className="text-lg font-bold text-gray-800">{Math.round(WAKTU_TOTAL / 60)}'</p><p className="text-xs text-gray-500">Waktu</p></div>
          <div><p className="text-lg font-bold text-gray-800">~72"</p><p className="text-xs text-gray-500">Per gambar</p></div>
        </div>

        <div className="flex gap-3">
          <button onClick={onBack} className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition">
            ← Kembali
          </button>
          <button onClick={onMulai} className="flex-grow py-3 rounded-xl font-bold border-2 border-purple-400 bg-purple-50 text-purple-700 hover:opacity-90 transition">
            Mulai Sekarang →
          </button>
        </div>
      </div>
    </div>
  );
}

function LayarHasil({ soalList, jawabanUser, onUlang, onBack, tryoutMode, tryoutId }) {
  const hasil = hitungSkorInterpretasiBentuk(soalList, jawabanUser);

  useEffect(() => {
    catatHasilTes('interpretasibentuk', hasil.skor, { dimensiPct: hasil.dimensiPct, totalDijawab: hasil.totalDijawab }, tryoutMode, tryoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
          <div className="text-4xl mb-2">🔮</div>
          <h2 className="text-lg font-bold text-gray-800 mb-1">Hasil Interpretasi Bentuk</h2>
          <p className="text-xs text-gray-400 mb-4">Gambaran kecenderungan pola jawaban, bukan penilaian benar/salah</p>
          <div className="grid grid-cols-2 gap-3 mb-2">
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-2xl font-extrabold text-gray-800">{hasil.totalDijawab}</p>
              <p className="text-xs text-gray-500">Gambar Dijawab</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-2xl font-extrabold text-gray-800">{hasil.pctSelesai}%</p>
              <p className="text-xs text-gray-500">Kelengkapan</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-sm font-bold text-purple-700 mb-3">📊 Distribusi Pola Jawaban</p>
          {URUTAN_DIMENSI_BENTUK.map(d => {
            const info = NAMA_DIMENSI_BENTUK[d];
            const pct = hasil.dimensiPct[d];
            return (
              <div key={d} className="mb-4 last:mb-0">
                <div className="flex justify-between items-start text-xs mb-1">
                  <span className="text-gray-700 font-semibold">{info.icon} {info.nama}</span>
                  <span className="font-bold text-purple-600">{pct === null ? '—' : `${pct}%`}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2 mb-1">
                  <div className="h-2 rounded-full bg-purple-500 transition-all" style={{ width: `${pct || 0}%` }} />
                </div>
                <p className="text-[11px] text-gray-500">{info.deskripsi}</p>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl shadow p-4">
          <div className="rounded-lg p-3 bg-amber-50 border border-amber-200">
            <p className="text-[11px] text-amber-700 leading-relaxed">⚠️ {DISCLAIMER_INTERPRETASI_BENTUK}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow p-4 flex gap-3">
          <button onClick={onUlang} className="flex-1 py-3 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 transition">
            🔄 Ulangi
          </button>
          <button onClick={onBack} className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition">
            ← Menu
          </button>
        </div>
      </div>
    </div>
  );
}

export default function InterpretasiBentukModul({ onBack, tryoutMode = false, tryoutId = null }) {
  const [fase, setFase]               = useState('intro');
  useCegahKeluarTes(fase === 'tes', onBack);
  const [soalList, setSoalList]       = useState([]);
  const [indeks, setIndeks]           = useState(0);
  const [jawabanUser, setJawabanUser] = useState([]);
  const [waktuSisa, setWaktuSisa]     = useState(WAKTU_TOTAL);
  const timerRef                      = useRef(null);
  const [progresTersimpan, setProgresTersimpan] = useState(null);

  useEffect(() => {
    setProgresTersimpan(bacaProgresTes('interpretasibentuk', tryoutId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useAutosaveTes(fase === 'tes', 'interpretasibentuk', tryoutId, {
    soalMeta: soalList.map(s => ({ id: s.id, opsi: s.opsi })),
    indeks,
    jawabanUser,
    waktuSisa,
  });

  const handleMulai = useCallback(() => {
    hapusProgresTes('interpretasibentuk', tryoutId);
    setProgresTersimpan(null);
    const soal = getSoalInterpretasiBentuk().slice(0, TOTAL_SOAL);
    setSoalList(soal);
    setIndeks(0);
    setJawabanUser(soal.map(s => ({ id: s.id, jawaban: null })));
    setWaktuSisa(WAKTU_TOTAL);
    setFase('tes');
  }, [tryoutId]);

  const handleLanjutkan = useCallback(() => {
    if (!progresTersimpan) return;
    const soal = soalDariMetaIB(progresTersimpan.soalMeta);
    if (soal.length === 0) { setProgresTersimpan(null); return; }
    setSoalList(soal);
    setIndeks(Math.min(progresTersimpan.indeks || 0, soal.length - 1));
    setJawabanUser(progresTersimpan.jawabanUser || soal.map(s => ({ id: s.id, jawaban: null })));
    setWaktuSisa(progresTersimpan.waktuSisa ?? WAKTU_TOTAL);
    setFase('tes');
  }, [progresTersimpan]);

  const handleMulaiBaruDariBanner = useCallback(() => {
    hapusProgresTes('interpretasibentuk', tryoutId);
    setProgresTersimpan(null);
  }, [tryoutId]);

  useEffect(() => {
    if (fase !== 'tes') return;
    timerRef.current = setInterval(() => {
      setWaktuSisa(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); hapusProgresTes('interpretasibentuk', tryoutId); setFase('hasil'); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [fase, tryoutId]);

  const handleJawab = useCallback((pilihanId) => {
    setJawabanUser(prev => prev.map(j => j.id === soalList[indeks].id ? { ...j, jawaban: pilihanId } : j));
    setIndeks(i => (i < soalList.length - 1 ? i + 1 : i));
  }, [indeks, soalList]);

  const handlePrev = useCallback(() => { if (indeks > 0) setIndeks(i => i - 1); }, [indeks]);
  const handleNext = useCallback(() => { if (indeks < soalList.length - 1) setIndeks(i => i + 1); }, [indeks, soalList.length]);

  const handleSelesai = useCallback(() => {
    clearInterval(timerRef.current);
    hapusProgresTes('interpretasibentuk', tryoutId);
    setFase('hasil');
  }, [tryoutId]);

  const handleUlang = useCallback(() => {
    setFase('intro'); setSoalList([]); setIndeks(0);
    setJawabanUser([]); setWaktuSisa(WAKTU_TOTAL);
  }, []);

  if (fase === 'intro') {
    return (
      <LayarIntro
        onMulai={handleMulai}
        onBack={onBack}
        tryoutMode={tryoutMode}
        progresTersimpan={progresTersimpan}
        onLanjutkan={handleLanjutkan}
        onMulaiBaru={handleMulaiBaruDariBanner}
      />
    );
  }

  if (fase === 'hasil') {
    return (
      <LayarHasil
        tryoutMode={tryoutMode}
        tryoutId={tryoutId}
        soalList={soalList}
        jawabanUser={jawabanUser}
        onUlang={handleUlang}
        onBack={onBack}
      />
    );
  }

  const soal = soalList[indeks];
  if (!soal) return null;

  const jawabanSoalIni = jawabanUser.find(j => j.id === soal.id)?.jawaban;
  const progress       = ((indeks + 1) / soalList.length) * 100;
  const warnaMeter      = waktuSisa < 60 ? 'bg-red-500' : waktuSisa < 180 ? 'bg-yellow-400' : 'bg-green-500';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className={`font-mono font-bold text-lg px-3 py-1 rounded-lg
              ${waktuSisa < 60 ? 'text-red-600 bg-red-50' : 'text-gray-700 bg-gray-100'}`}>
              ⏱ {formatWaktu(waktuSisa)}
            </div>
            <span className="text-sm text-gray-500 font-semibold">
              Gambar <span className="text-purple-600">{indeks + 1}</span> / {soalList.length}
            </span>
            <span className="text-xs font-bold px-2 py-1 rounded-full bg-purple-100 text-purple-700">
              🔮 Interpretasi
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className={`h-2 rounded-full transition-all duration-300 ${warnaMeter}`} style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        <div className="bg-white rounded-2xl shadow p-6 mb-4">
          <p className="text-xs text-gray-400 text-center mb-3 uppercase tracking-wider">Menurutmu, gambar ini paling mirip apa?</p>
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 mb-4 flex items-center justify-center">
            <div className="w-full max-w-[220px]" style={{ aspectRatio: '200 / 140' }} dangerouslySetInnerHTML={{ __html: soal.gambar }} />
          </div>

          <div className="grid grid-cols-1 gap-2">
            {soal.opsi.map(o => (
              <button
                key={o.id}
                onClick={() => handleJawab(o.id)}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 font-medium transition text-sm
                  ${jawabanSoalIni === o.id
                    ? 'border-purple-500 bg-purple-100 text-purple-800'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-purple-300 hover:bg-purple-50'}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-3 mb-4">
          <button
            onClick={handlePrev}
            disabled={indeks === 0}
            className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ← Sebelumnya
          </button>
          {indeks < soalList.length - 1 ? (
            <button onClick={handleNext} className="flex-grow py-3 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 transition">
              Selanjutnya →
            </button>
          ) : (
            <button onClick={handleSelesai} className="flex-grow py-3 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 transition">
              Selesai ✓
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-1 justify-center">
          {soalList.map((s, i) => {
            const dijawab = jawabanUser.find(j => j.id === s.id)?.jawaban;
            return (
              <button
                key={s.id}
                onClick={() => setIndeks(i)}
                className={`w-8 h-8 text-xs rounded-lg font-semibold transition
                  ${i === indeks ? 'bg-purple-600 text-white'
                  : dijawab      ? 'bg-green-500 text-white'
                  : 'bg-purple-100 text-purple-600 hover:bg-purple-200'}`}
              >
                {i + 1}
              </button>
            );
          })}
        </div>
        <p className="text-center text-xs text-gray-400 mt-2">
          🟪 Aktif &nbsp;|&nbsp; 🟩 Sudah dijawab &nbsp;|&nbsp; ⬜ Belum dijawab
        </p>
      </div>
    </div>
  );
}
